
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Discrepancy } from "@/types/discrepancy";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface RejectDiscrepancyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  discrepancy: Discrepancy;
  rejectType: "salary" | "forgive";
  onSubmit: (options: {
    isSalaryDeduction: boolean;
    salaryDeductionEmployee?: string;
    isForgiven: boolean;
  }) => void;
}

export const RejectDiscrepancyDialog = ({
  open,
  onOpenChange,
  discrepancy,
  rejectType,
  onSubmit,
}: RejectDiscrepancyDialogProps) => {
  const [employeeName, setEmployeeName] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (rejectType === "salary" && !employeeName.trim()) {
      toast.error("Veuillez entrer le nom de l'employé");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Utilisateur non authentifié");
        return;
      }

      // First validate the counted amount
      const { error: validationError } = await supabase
        .from("validated_amounts")
        .insert({
          counting_date: discrepancy.counting_date,
          payment_method: discrepancy.payment_method,
          amount: discrepancy.counted_amount,
          status: "validated",
          owner_id: user.id
        });

      if (validationError) {
        console.error("Error validating amount:", validationError);
        toast.error("Erreur lors de la validation du montant");
        return;
      }

      // Update poultry transactions to mark them as validated revenue
      const { error: transactionError } = await supabase
        .from("poultry_transactions")
        .update({ is_deducted_from_revenue: true })
        .eq("created_by", user.id)
        .eq("created_at", discrepancy.counting_date);

      if (transactionError) {
        console.error("Error updating transactions:", transactionError);
        toast.error("Erreur lors de la mise à jour des transactions");
        return;
      }

      // Then submit with the correct status based on reject type
      onSubmit({
        isSalaryDeduction: rejectType === "salary",
        salaryDeductionEmployee: rejectType === "salary" ? employeeName : undefined,
        isForgiven: rejectType === "forgive",
      });

      toast.success("Montant validé avec succès");
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      toast.error("Une erreur est survenue");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {rejectType === "salary" ? "Retenue sur salaire" : "Pardonner la différence"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {rejectType === "salary" && (
            <div className="space-y-2">
              <Label htmlFor="employee">Nom de l'employé</Label>
              <Input
                id="employee"
                value={employeeName}
                onChange={(e) => setEmployeeName(e.target.value)}
                placeholder="Entrez le nom de l'employé"
                required
              />
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <Button type="submit" variant="destructive">
              Confirmer
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
